import { createFileRoute } from "@tanstack/react-router";
import { useCallback, useEffect, useMemo, useState } from "react";
import { useIncrementalList } from "@/hooks/use-incremental-list";
import { toast } from "sonner";
import { Plus, Loader2, Search, Trash2, GraduationCap } from "lucide-react";
import { AdminShell } from "@/components/admin/admin-shell";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle, DialogTrigger } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Badge } from "@/components/ui/badge";
import { requireSchoolAdminOrOnboard, useSchoolContext } from "@/lib/school-context";
import { supabase } from "@/integrations/supabase/client";
import type { Database } from "@/integrations/supabase/types";
import { AICallout } from "@/components/makai/primitives";

type LearnerRow = Database["public"]["Tables"]["learners"]["Row"] & {
  current_stream: { name: string; class: { name: string } | null } | null;
};

export const Route = createFileRoute("/admin/learners")({
  ssr: false,
  beforeLoad: () => requireSchoolAdminOrOnboard(),
  head: () => ({ meta: [{ title: "Learners — MAKAI" }] }),
  component: LearnersPage,
});

function LearnersPage() {
  const { active } = useSchoolContext();
  const [rows, setRows] = useState<LearnerRow[]>([]);
  const [streams, setStreams] = useState<{ id: string; name: string; class: { name: string } | null }[]>([]);
  const [q, setQ] = useState("");
  const [status, setStatus] = useState<"all" | Database["public"]["Enums"]["learner_status"]>("active");

  const load = useCallback(async () => {
    if (!active) return;
    let qb = supabase.from("learners").select("*, current_stream:streams(name, class:classes(name))").eq("school_id", active.school_id).order("last_name");
    if (status !== "all") qb = qb.eq("status", status);
    const { data } = await qb;
    setRows((data ?? []) as unknown as LearnerRow[]);
    const { data: st } = await supabase.from("streams").select("id,name,class:classes(name)").eq("school_id", active.school_id);
    setStreams((st ?? []) as typeof streams);
  }, [active, status]);
  useEffect(() => { load(); }, [load]);

  const filtered = useMemo(
    () => rows.filter((r) => !q || `${r.first_name} ${r.last_name} ${r.admission_number ?? ""}`.toLowerCase().includes(q.toLowerCase())),
    [rows, q],
  );
  const learnerRows = useIncrementalList(filtered, 40);

  return (
    <AdminShell title="Learners" subtitle="Admit, profile, promote and transfer learners." action={<AdmitDialog streams={streams} onSaved={load} />}>
      <div className="mb-4 flex flex-col gap-2 sm:flex-row">
        <div className="relative flex-1">
          <Search className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
          <Input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search by name or admission no." className="pl-9" />
        </div>
        <Select value={status} onValueChange={(v) => setStatus(v as typeof status)}>
          <SelectTrigger className="sm:w-48"><SelectValue /></SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All statuses</SelectItem>
            {["active","graduated","transferred","suspended","withdrawn"].map((s) => <SelectItem key={s} value={s} className="capitalize">{s}</SelectItem>)}
          </SelectContent>
        </Select>
      </div>

      {filtered.length === 0 ? (
        <div className="rounded-2xl border border-dashed border-border p-10 text-center text-sm text-muted-foreground">No learners match your filter.</div>
      ) : (
        <div className="overflow-x-auto rounded-2xl border border-border bg-card shadow-soft">
          <table className="w-full text-sm">
            <thead className="bg-muted text-left text-xs uppercase tracking-wider text-muted-foreground">
              <tr><th className="px-4 py-2.5">Admission</th><th className="px-4 py-2.5">Name</th><th className="px-4 py-2.5">Class / Stream</th><th className="px-4 py-2.5">Gender</th><th className="px-4 py-2.5">Status</th><th className="px-4 py-2.5" /></tr>
            </thead>
            <tbody>
              {learnerRows.visible.map((l) => (
                <tr key={l.id} className="border-t border-border">
                  <td className="px-4 py-3 font-mono text-xs text-muted-foreground">{l.admission_number ?? "—"}</td>
                  <td className="px-4 py-3 font-semibold">{l.first_name} {l.last_name}</td>
                  <td className="px-4 py-3 text-muted-foreground">{l.current_stream ? `${l.current_stream.class?.name ?? ""} ${l.current_stream.name}` : "—"}</td>
                  <td className="px-4 py-3 capitalize text-muted-foreground">{l.gender ?? "—"}</td>
                  <td className="px-4 py-3"><Badge variant={l.status === "active" ? "default" : "outline"} className="capitalize">{l.status}</Badge></td>
                  <td className="px-4 py-3 text-right">
                    <PromoteDialog learner={l} streams={streams} onSaved={load} />
                    <Button size="sm" variant="ghost" className="text-destructive" onClick={async () => {
                      if (!confirm("Withdraw this learner?")) return;
                      const { error } = await supabase.from("learners").update({ status: "withdrawn" }).eq("id", l.id);
                      if (error) toast.error(error.message); else load();
                    }}><Trash2 className="h-3.5 w-3.5" /></Button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
          {learnerRows.hasMore && (
            <div ref={learnerRows.sentinelRef} className="py-4 text-center text-xs text-muted-foreground">
              Loading more learners…
            </div>
          )}
        </div>
      )}

      <div className="mt-6">
        <AICallout
          description="Spot at-risk learners, summarise profiles and draft parent notes."
          suggestions={[
            "Which students are at risk?",
            "Summarise this learner",
            "Identify strengths and risks across the cohort",
            "Suggest interventions",
          ]}
        />
      </div>
    </AdminShell>
  );
}

function AdmitDialog({ streams, onSaved }: { streams: { id: string; name: string; class: { name: string } | null }[]; onSaved: () => void }) {
  const { active } = useSchoolContext();
  const [open, setOpen] = useState(false);
  const [busy, setBusy] = useState(false);
  const [form, setForm] = useState({
    first_name: "", middle_name: "", last_name: "", dob: "", gender: "" as "" | "male" | "female" | "other",
    admission_number: "", admission_date: new Date().toISOString().slice(0,10),
    current_stream_id: "" as string, address: "", phone: "", email: "", medical_notes: "",
  });

  async function submit(e: React.FormEvent) {
    e.preventDefault();
    if (!active || !form.first_name || !form.last_name) { toast.error("Names required"); return; }
    setBusy(true);
    let admission_number = form.admission_number;
    if (!admission_number) {
      const { data } = await supabase.rpc("next_school_number", { _school: active.school_id, _key: "admission_seq", _prefix: "ADM" });
      admission_number = (data as string) || "";
    }
    const { error } = await supabase.from("learners").insert({
      school_id: active.school_id,
      first_name: form.first_name, middle_name: form.middle_name || null, last_name: form.last_name,
      dob: form.dob || null, gender: form.gender || null,
      admission_number, admission_date: form.admission_date || null,
      current_stream_id: form.current_stream_id || null,
      address: form.address || null, phone: form.phone || null, email: form.email || null,
      medical_notes: form.medical_notes || null,
    });
    setBusy(false);
    if (error) { toast.error(error.message); return; }
    toast.success("Learner admitted"); setOpen(false); onSaved();
    setForm({ first_name: "", middle_name: "", last_name: "", dob: "", gender: "", admission_number: "", admission_date: new Date().toISOString().slice(0,10), current_stream_id: "", address: "", phone: "", email: "", medical_notes: "" });
  }

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild><Button className="bg-primary hover:bg-primary/90"><Plus className="mr-1.5 h-4 w-4" /> Admit learner</Button></DialogTrigger>
      <DialogContent className="max-w-lg">
        <DialogHeader><DialogTitle>Admit new learner</DialogTitle></DialogHeader>
        <form className="grid gap-3 sm:grid-cols-2" onSubmit={submit}>
          <Field label="First name *"><Input value={form.first_name} onChange={(e) => setForm({ ...form, first_name: e.target.value })} required /></Field>
          <Field label="Last name *"><Input value={form.last_name} onChange={(e) => setForm({ ...form, last_name: e.target.value })} required /></Field>
          <Field label="Middle name" className="sm:col-span-2"><Input value={form.middle_name} onChange={(e) => setForm({ ...form, middle_name: e.target.value })} /></Field>
          <Field label="Date of birth"><Input type="date" value={form.dob} onChange={(e) => setForm({ ...form, dob: e.target.value })} /></Field>
          <Field label="Gender">
            <Select value={form.gender} onValueChange={(v) => setForm({ ...form, gender: v as typeof form.gender })}>
              <SelectTrigger><SelectValue placeholder="—" /></SelectTrigger>
              <SelectContent><SelectItem value="male">Male</SelectItem><SelectItem value="female">Female</SelectItem><SelectItem value="other">Other</SelectItem></SelectContent>
            </Select>
          </Field>
          <Field label="Admission no."><Input value={form.admission_number} onChange={(e) => setForm({ ...form, admission_number: e.target.value })} placeholder="Auto if blank" /></Field>
          <Field label="Admission date"><Input type="date" value={form.admission_date} onChange={(e) => setForm({ ...form, admission_date: e.target.value })} /></Field>
          <Field label="Class / Stream" className="sm:col-span-2">
            <Select value={form.current_stream_id} onValueChange={(v) => setForm({ ...form, current_stream_id: v })}>
              <SelectTrigger><SelectValue placeholder="Pick a stream" /></SelectTrigger>
              <SelectContent>{streams.map((s) => <SelectItem key={s.id} value={s.id}>{s.class?.name ?? ""} {s.name}</SelectItem>)}</SelectContent>
            </Select>
          </Field>
          <Field label="Phone"><Input value={form.phone} onChange={(e) => setForm({ ...form, phone: e.target.value })} /></Field>
          <Field label="Email"><Input type="email" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} /></Field>
          <Field label="Address" className="sm:col-span-2"><Textarea rows={2} value={form.address} onChange={(e) => setForm({ ...form, address: e.target.value })} /></Field>
          <Field label="Medical notes" className="sm:col-span-2"><Textarea rows={2} value={form.medical_notes} onChange={(e) => setForm({ ...form, medical_notes: e.target.value })} /></Field>
          <DialogFooter className="sm:col-span-2"><Button type="submit" disabled={busy} className="bg-primary hover:bg-primary/90">{busy && <Loader2 className="mr-2 h-4 w-4 animate-spin" />} Admit</Button></DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}

function PromoteDialog({ learner, streams, onSaved }: { learner: LearnerRow; streams: { id: string; name: string; class: { name: string } | null }[]; onSaved: () => void }) {
  const { active } = useSchoolContext();
  const [open, setOpen] = useState(false);
  const [busy, setBusy] = useState(false);
  const [form, setForm] = useState({ to_stream_id: "", type: "promotion" as Database["public"]["Enums"]["promotion_type"], notes: "" });

  async function submit(e: React.FormEvent) {
    e.preventDefault();
    if (!active) return;
    setBusy(true);
    const updates: Database["public"]["Tables"]["learners"]["Update"] = {};
    if (form.type === "graduation") updates.status = "graduated";
    if (form.type === "transfer") updates.status = "transferred";
    if (form.to_stream_id) updates.current_stream_id = form.to_stream_id;
    const [{ error: e1 }, { error: e2 }] = await Promise.all([
      supabase.from("promotions").insert({
        school_id: active.school_id, learner_id: learner.id,
        from_stream_id: learner.current_stream_id, to_stream_id: form.to_stream_id || null,
        type: form.type, notes: form.notes || null,
      }),
      Object.keys(updates).length ? supabase.from("learners").update(updates).eq("id", learner.id) : Promise.resolve({ error: null } as { error: null }),
    ]);
    setBusy(false);
    if (e1 || e2) { toast.error((e1 ?? e2)!.message); return; }
    toast.success("Recorded"); setOpen(false); onSaved();
  }

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild><Button size="sm" variant="ghost"><GraduationCap className="h-3.5 w-3.5" /></Button></DialogTrigger>
      <DialogContent className="max-w-md">
        <DialogHeader><DialogTitle>Promote / transfer / graduate</DialogTitle></DialogHeader>
        <form className="space-y-3" onSubmit={submit}>
          <Field label="Action">
            <Select value={form.type} onValueChange={(v) => setForm({ ...form, type: v as typeof form.type })}>
              <SelectTrigger><SelectValue /></SelectTrigger>
              <SelectContent>{["promotion","transfer","graduation","repeat","demotion"].map((t) => <SelectItem key={t} value={t} className="capitalize">{t}</SelectItem>)}</SelectContent>
            </Select>
          </Field>
          {form.type !== "graduation" && (
            <Field label="To stream">
              <Select value={form.to_stream_id} onValueChange={(v) => setForm({ ...form, to_stream_id: v })}>
                <SelectTrigger><SelectValue placeholder="Pick a stream" /></SelectTrigger>
                <SelectContent>{streams.map((s) => <SelectItem key={s.id} value={s.id}>{s.class?.name ?? ""} {s.name}</SelectItem>)}</SelectContent>
              </Select>
            </Field>
          )}
          <Field label="Notes"><Textarea rows={2} value={form.notes} onChange={(e) => setForm({ ...form, notes: e.target.value })} /></Field>
          <DialogFooter><Button type="submit" disabled={busy} className="bg-primary hover:bg-primary/90">{busy && <Loader2 className="mr-2 h-4 w-4 animate-spin" />} Confirm</Button></DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}

function Field({ label, children, className }: { label: string; children: React.ReactNode; className?: string }) {
  return (<div className={"space-y-1.5 " + (className ?? "")}><Label className="text-xs font-semibold uppercase tracking-wider text-muted-foreground">{label}</Label>{children}</div>);
}
